// src/components/BalanceCard.jsx - Keep all features, add mobile optimization
import { Users, CheckCircle, AlertCircle } from 'lucide-react';
import { sanitizeString } from '../utils/helpers';

export default function BalanceCard({ dashboardData }) {
  const { 
    member, 
    isHousehold, 
    householdMemberNames = [], 
    creditBalance,
    amountOwed
  } = dashboardData;

  const displayMemberName = isHousehold && householdMemberNames.length > 0
    ? `${sanitizeString(member.name)} & ${householdMemberNames.map(sanitizeString).join(', ')}`
    : sanitizeString(member.name);

  // Inconsistent state warning
  if (creditBalance > 0 && amountOwed > 0) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 sm:p-5 border border-red-300 shadow-md">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <p className="text-sm font-semibold text-red-900 mb-2">Balance Review Needed</p>
            <p className="text-sm text-red-800">
              Your account shows both credit and owed amounts. Please visit the welcome center 
              and ask the CBA group leader to review your balance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal balance card - keep all original features, just add mobile spacing
  return (
    <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
      {/* Member Info Section */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex-1 min-w-0">
          <p className="text-gray-500 text-xs sm:text-sm font-medium mb-1">Welcome back,</p>
          <h2 className="text-base sm:text-lg font-bold text-gray-800 truncate">
            {displayMemberName}
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            {isHousehold ? 'Household' : 'Individual'} Account
          </p>
        </div>
        <Users className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 flex-shrink-0 ml-3" />
      </div>

      {/* Balance Display */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 border border-gray-200">
        <div className="flex items-center gap-2 mb-2 sm:mb-3">
          {amountOwed > 0 ? (
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
          ) : (
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
          )}
          <span className="text-xs sm:text-sm font-medium text-gray-600">
            {amountOwed > 0 ? 'Amount Owed' : 'Credit Balance'}
          </span>
        </div>
        
        <div className={`text-3xl sm:text-4xl font-bold ${amountOwed > 0 ? 'text-orange-600' : 'text-green-600'}`}>
          ${amountOwed > 0 ? amountOwed.toFixed(2) : creditBalance.toFixed(2)}
        </div>
      </div>
    </div>
  );
}